/**
 * /emails/configuracao — Configuracao de gateway de email + flags opt-in (M11.3).
 *
 * Permite gestor:
 *   - Escolher provider (mock / sendgrid / mailgun / ses)
 *   - Cadastrar API key (cifrada Fernet no backend)
 *   - Setar from_email + from_nome + reply_to
 *   - Trocar ambiente HOMOLOGACAO/PRODUCAO (HOMOLOGACAO forca mock)
 *   - Ligar/desligar 5 flags de notificacao automatica
 *   - Testar envio de email
 *
 * Permissao: email:configurar (Master + gestor sistema).
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  FiAlertTriangle, FiCheckCircle, FiMail, FiSave, FiSend,
} from "react-icons/fi";
import styled from "styled-components";

import AsyncButton from "../../../components/AsyncButton";
import EntityHeader from "../../../components/EntityHeader";
import { useAuth } from "../../../context/AuthContext";
import { PageContainer } from "../../../styles/shared";

import {
  Card, ErrorMsg, Input, Pill, SectionTitle, Select,
} from "../../Orcamentos/styles";

import {
  AMBIENTES_EMAIL,
  PROVIDERS_EMAIL,
  atualizarConfiguracaoEmail,
  criarConfiguracaoEmail,
  listarConfiguracoesEmail,
  testarEnvioEmail,
} from "../../../services/emails";


const FLAGS_NOTIFICACAO = [
  {
    key: "notificar_orcamento_enviado",
    label: "Orçamento enviado ao cliente",
    descricao: "Email pro cliente com link público quando orçamento eh enviado.",
  },
  {
    key: "notificar_contrato_assinado",
    label: "Contrato assinado",
    descricao: "Email pro vendedor quando contrato eh assinado digitalmente.",
  },
  {
    key: "notificar_nfe_emitida",
    label: "NF-e emitida",
    descricao: "Email pro cliente com link DANFE/XML quando NF-e eh autorizada no SEFAZ.",
  },
  {
    key: "notificar_boleto_proximo_vencimento",
    label: "Boleto próximo do vencimento",
    descricao: "Aviso pro cliente 3 e 1 dia antes do vencimento (cron diario).",
  },
  {
    key: "notificar_assistencia_aberta",
    label: "Assistencia aberta",
    descricao: "Email pro responsável quando assistencia eh criada.",
  },
  {
    key: "notificar_pesquisa_satisfacao",
    label: "Pesquisa de satisfação (NPS)",
    descricao:
      "Link da pesquisa pro cliente quando o pedido eh entregue. Sem ele, o "
      + "link só chega se alguém copiar a mão, pedido a pedido.",
  },
];


export default function ConfiguracaoEmails() {
  const { request, hasAllPermissions, isMaster } = useAuth();

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [testando, setTestando] = useState(false);
  const [destinatarioTeste, setDestinatarioTeste] = useState("");
  const [apiKeyPlain, setApiKeyPlain] = useState("");

  const podeConfigurar = isMaster || hasAllPermissions(["email:configurar"]);

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = await listarConfiguracoesEmail(request);
      const list = Array.isArray(p) ? p : (p?.results || []);
      if (list.length > 0) {
        setConfig(list[0]);
      } else {
        // Vazio = formulario em branco pra criar
        setConfig({
          provider: "mock",
          ambiente: "HOMOLOGACAO",
          from_email: "",
          from_nome: "",
          reply_to: "",
          ativo: true,
          notificar_orcamento_enviado: true,
          notificar_contrato_assinado: true,
          notificar_nfe_emitida: true,
          notificar_boleto_proximo_vencimento: false,
          notificar_assistencia_aberta: true,
          notificar_pesquisa_satisfacao: true,
        });
      }
    } catch (err) {
      setError(err?.message || "Falha ao carregar configuração.");
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async () => {
    if (!config) return;
    const payload = { ...config };
    if (apiKeyPlain) {
      payload.api_key_plain = apiKeyPlain;
    }
    try {
      if (config.id) {
        const atualizada = await atualizarConfiguracaoEmail(request, config.id, payload);
        setConfig(atualizada);
      } else {
        const criada = await criarConfiguracaoEmail(request, payload);
        setConfig(criada);
      }
      setApiKeyPlain("");
    } catch (err) {
      setError(err?.message || "Falha ao salvar.");
      throw err;
    }
  };

  const testar = async () => {
    if (!config?.id || !destinatarioTeste) return;
    setTestando(true);
    try {
      await testarEnvioEmail(request, config.id, destinatarioTeste);
    } finally {
      setTestando(false);
    }
  };

  if (loading || !config) {
    return (
      <PageContainer>
        <EntityHeader title="Configuração de E-mail" icone={FiMail} />
        <Card><p>Carregando...</p></Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <EntityHeader
        title="Configuração de E-mail"
        subtitle={
          config.id
            ? `Provider: ${config.provider} • ${config.ambiente}`
            : "Nenhuma configuração — preencha para criar."
        }
        icone={FiMail}
      />

      {error && <ErrorMsg>{error}</ErrorMsg>}

      <Card>
        <SectionTitle>Gateway</SectionTitle>
        <FieldRow>
          <Field>
            <Label>Provider</Label>
            <Select
              value={config.provider}
              onChange={(e) => setConfig({ ...config, provider: e.target.value })}
              disabled={!podeConfigurar}
            >
              {PROVIDERS_EMAIL.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </Select>
          </Field>
          <Field>
            <Label>Ambiente</Label>
            <Select
              value={config.ambiente}
              onChange={(e) => setConfig({ ...config, ambiente: e.target.value })}
              disabled={!podeConfigurar}
            >
              {AMBIENTES_EMAIL.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </Select>
            {config.ambiente === "HOMOLOGACAO" && (
              <Hint>
                <FiAlertTriangle size={12} /> Homologacao FORCA mock automaticamente — nao envia email real.
              </Hint>
            )}
          </Field>
        </FieldRow>

        <FieldRow>
          <Field>
            <Label>API Key (write-only)</Label>
            <Input
              type="password"
              value={apiKeyPlain}
              onChange={(e) => setApiKeyPlain(e.target.value)}
              placeholder={
                config.api_key_configurada
                  ? "**** configurada (deixe vazio para não alterar)"
                  : "Cole a API key aqui"
              }
              disabled={!podeConfigurar}
            />
            {config.api_key_configurada && (
              <Pill $tone="success">
                <FiCheckCircle size={11} /> API key configurada
              </Pill>
            )}
          </Field>
        </FieldRow>
      </Card>

      <Card>
        <SectionTitle>Remetente</SectionTitle>
        <FieldRow>
          <Field>
            <Label>From email *</Label>
            <Input
              value={config.from_email || ""}
              onChange={(e) => setConfig({ ...config, from_email: e.target.value })}
              placeholder="comercial@suaempresa.com.br"
              disabled={!podeConfigurar}
            />
          </Field>
          <Field>
            <Label>From nome</Label>
            <Input
              value={config.from_nome || ""}
              onChange={(e) => setConfig({ ...config, from_nome: e.target.value })}
              placeholder="Sua Marcenaria"
              disabled={!podeConfigurar}
            />
          </Field>
        </FieldRow>
        <FieldRow>
          <Field>
            <Label>Reply-To (opcional)</Label>
            <Input
              value={config.reply_to || ""}
              onChange={(e) => setConfig({ ...config, reply_to: e.target.value })}
              placeholder="(usa from_email se vazio)"
              disabled={!podeConfigurar}
            />
          </Field>
        </FieldRow>
      </Card>

      <Card>
        <SectionTitle>Notificações automaticas (opt-in)</SectionTitle>
        {FLAGS_NOTIFICACAO.map((flag) => (
          <FlagRow key={flag.key}>
            <input
              type="checkbox"
              checked={config[flag.key]}
              onChange={(e) => setConfig({ ...config, [flag.key]: e.target.checked })}
              disabled={!podeConfigurar}
              id={flag.key}
            />
            <div>
              <label htmlFor={flag.key}>
                <strong>{flag.label}</strong>
              </label>
              <FlagHint>{flag.descricao}</FlagHint>
            </div>
          </FlagRow>
        ))}
      </Card>

      {podeConfigurar && (
        <Card>
          <SectionTitle>Ações</SectionTitle>
          <ActionRow>
            <AsyncButton
              variant="primary"
              onClick={salvar}
              successMessage="Configuração salva."
            >
              <FiSave size={14} /> Salvar
            </AsyncButton>
            {config.id && (
              <>
                <Input
                  type="email"
                  placeholder="destinatario@teste.com"
                  value={destinatarioTeste}
                  onChange={(e) => setDestinatarioTeste(e.target.value)}
                  style={{ flex: 1 }}
                />
                <AsyncButton
                  variant="secondary"
                  onClick={testar}
                  disabled={!destinatarioTeste || testando}
                  successMessage="Email de teste enviado."
                  errorMessage="Falha no envio de teste."
                >
                  <FiSend size={14} /> Testar envio
                </AsyncButton>
              </>
            )}
          </ActionRow>
        </Card>
      )}
    </PageContainer>
  );
}


// Local styles
const FieldRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 12px;
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const Label = styled.label`
  font-size: 0.78rem;
  font-weight: 600;
  color: ${({ theme }) => theme.textSecondary};
`;
const Hint = styled.span`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.textSecondary};
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
`;
const FlagRow = styled.div`
  display: grid;
  grid-template-columns: 24px 1fr;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  &:last-child { border-bottom: none; }
  input[type="checkbox"] {
    margin-top: 4px;
  }
  label { cursor: pointer; }
`;
const FlagHint = styled.div`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.textSecondary};
  margin-top: 2px;
`;
const ActionRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;
